// Modal do QR Code no padrão Aeros Dark: QR à esquerda, instruções numeradas
// à direita (mockup "Telas Aeros Dark - Atendimentos e Conexões").
//
// O mockup mostra um contador "expira em 0:42". O backend não informa quando
// o código expira — quem renova é o socket `whatsappSession`, empurrando um
// qrcode novo — então aqui fica só a frase de renovação automática, sem
// inventar um relógio que não corresponde ao que acontece.
import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import TimerIcon from "@material-ui/icons/Timer";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { channelLabel } from "../../pages/Connections/connectionRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    paper: {
      width: 700,
      maxWidth: "calc(100vw - 32px)",
      margin: 16,
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "260px minmax(0,1fr)",
      gap: 20,
      padding: 24,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    qrBox: {
      backgroundColor: "#FFFFFF",
      borderRadius: 14,
      padding: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "start",
      aspectRatio: "1 / 1",
    },
    waiting: {
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
      textAlign: "center",
    },
    head: { display: "flex", alignItems: "flex-start", gap: 8 },
    title: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary,
    },
    subtitle: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      margin: "6px 0 14px",
    },
    connName: { color: theme.palette.text.primary, fontWeight: 700 },
    steps: {
      margin: 0,
      paddingLeft: 18,
      fontSize: 12.5,
      lineHeight: 1.9,
      color: theme.palette.text.secondary,
      "& b": { color: theme.palette.text.primary, fontWeight: 600 },
    },
    renew: {
      marginTop: 14,
      display: "flex",
      alignItems: "center",
      gap: 7,
      fontSize: 11.5,
      padding: "8px 12px",
      borderRadius: 10,
      color: stateText(theme, AEROS.cyan, "info"),
      backgroundColor: "rgba(34,211,238,0.08)",
      border: `1px solid rgba(34,211,238,0.2)`,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    actions: {
      marginTop: 16,
      paddingTop: 16,
      borderTop: `1px solid ${border}`,
      display: "flex",
      gap: 10,
    },
    closeButton: {
      marginLeft: "auto",
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
  };
});

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [qrCode, setQrCode] = useState("");
  const [whatsApp, setWhatsApp] = useState(null);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
        setWhatsApp(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    if (!whatsAppId) return;
    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
      }

      if (data.action === "update" && data.session.qrcode === "") {
        onClose();
      }
    };
    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose, user.companyId]);

  const handleNewQrCode = async () => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.paper }}
      aria-labelledby="qr-code-dialog"
    >
      <div className={classes.grid}>
        <div className={classes.qrBox}>
          {qrCode ? (
            <QRCode
              value={qrCode}
              size={232}
              style={{ maxWidth: "100%", height: "auto" }}
            />
          ) : (
            <div className={classes.waiting}>{i18n.t("qrCode.waiting")}</div>
          )}
        </div>

        <div>
          <div className={classes.head}>
            <div className={classes.title} id="qr-code-dialog">
              {i18n.t("qrCode.message")}
            </div>
            <IconButton
              className={classes.closeButton}
              onClick={onClose}
              aria-label={i18n.t("aeros.header.close")}
            >
              <CloseIcon />
            </IconButton>
          </div>

          {whatsApp && (
            <div className={classes.subtitle}>
              {i18n.t("aeros.connections.qr.connection")}{" "}
              <span className={classes.connName}>{whatsApp.name}</span> ·{" "}
              {channelLabel(whatsApp.channel)}
            </div>
          )}

          <ol className={classes.steps}>
            <li>{i18n.t("aeros.connections.qr.step1")}</li>
            <li>{i18n.t("aeros.connections.qr.step2")}</li>
            <li>{i18n.t("aeros.connections.qr.step3")}</li>
          </ol>

          <div className={classes.renew}>
            <TimerIcon />
            <span>{i18n.t("aeros.connections.qr.autoRenew")}</span>
          </div>

          <div className={classes.actions}>
            <Button
              className={m.cancelButton}
              startIcon={<RefreshIcon />}
              onClick={handleNewQrCode}
            >
              {i18n.t("aeros.connections.qr.newCode")}
            </Button>
            <Button className={m.cancelButton} onClick={onClose}>
              {i18n.t("aeros.header.close")}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
